import { BASE_URL } from '@/utils/Constants';
import { useAuth } from '@/utils/stores/useAuthStore';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

const uploadDoc = async (data: string, userId: string, token: string) => {
  const response = await fetch(`${BASE_URL}/api/documents`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ data, user_id: userId }),
  });

  if (!response.ok) {
    const error = new Error('Failed to upload document');
    (error as any).status = response.status;
    throw error;
  }

  return response.json();
};

export const useUploadDoc = () => {
  const { userId, idToken } = useAuth();

  // Progress state for fake progress bar
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const mutation = useMutation({
    mutationFn: (data: string) => uploadDoc(data, userId!, idToken!),

    onMutate: () => {
      setProgress(0);
      timerRef.current = setInterval(() => {
        setProgress((old) => (old < 90 ? old + Math.random() * 3 : old));
      }, 400);
    },

    onSettled: () => {
      if (timerRef.current) clearInterval(timerRef.current);
      setProgress(100);

      // Reset progress after a little delay so UI can show 100%
      setTimeout(() => setProgress(0), 1000);
    },
  });

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    progress,
  };
};